import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { JobApiResponse, TodoResponse } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CheckSquare, ChevronDown, ChevronUp, Filter, Plus, Search, X } from "lucide-react";
import { useMemo, useState } from "react";
import { DraggableTodoCard, TodoCard } from './TodoCard';
import { TodoForm } from './TodoForm';

interface TodoListProps {
  todos: TodoResponse[];
  jobs: JobApiResponse[];
  onToggleComplete: (todoId: string, completed: boolean) => void;
  onEdit: (todo: TodoResponse) => void;
  onDelete: (todoId: string) => void;
  onUpdate?: (todoId: string, updates: Partial<TodoResponse>) => void;
  onReorder?: (todoIds: string[]) => void;
  onCreateNew: () => void;
  isLoading?: boolean;
  embedded?: boolean;
  fixedJobId?: string; // when provided, auto-filter to this job and hide job filter
}

type TodoColumn = 'done' | 'overdue' | 'due-soon' | 'due-later';

interface CompletionState {
  [todoId: string]: 'idle' | 'confirming';
}

interface ActionState {
  [todoId: string]: boolean;
}

// Utility function to generate consistent colors from tag names
const getTagColor = (tagName: string): string => {
  const colors = [
    '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
    '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
  ];
  
  let hash = 0;
  for (let i = 0; i < tagName.length; i++) {
    hash = tagName.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

export const TodoList: React.FC<TodoListProps> = ({
  todos,
  jobs,
  onToggleComplete,
  onEdit,
  onDelete,
  onUpdate,
  onReorder,
  onCreateNew,
  isLoading = false,
  embedded = false,
  fixedJobId,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedJobId, setSelectedJobId] = useState<string>(fixedJobId ?? 'all');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [editingTodo, setEditingTodo] = useState<TodoResponse | null>(null);
  const [openCalendar, setOpenCalendar] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [completionStates, setCompletionStates] = useState<CompletionState>({})
  const [actionStates, setActionStates] = useState<ActionState>({});
  const [overId, setOverId] = useState<string | null>(null);
  const [insertAfter, setInsertAfter] = useState<boolean>(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );



  // Get all unique tags from todos
  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    todos.forEach(todo => {
      todo.tags.forEach(tag => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [todos]);

  // Filter todos based on search, tags, and job
  const filteredTodos = useMemo(() => {
    let filtered = todos;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(todo =>
        todo.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        todo.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        todo.job_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        todo.job_company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        todo.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Apply tag filter
    if (selectedTags.length > 0) {
      filtered = filtered.filter(todo =>
        selectedTags.some(selectedTag => todo.tags.includes(selectedTag))
      );
    }

    // Apply job filter
    if ((selectedJobId && selectedJobId !== 'all') || fixedJobId) {
      if (selectedJobId === 'general') {
        filtered = filtered.filter(todo => !todo.job_id);
      } else {
        const targetId = fixedJobId ?? selectedJobId;
        filtered = filtered.filter(todo => todo.job_id === targetId);
      }
    }

    return filtered;
  }, [todos, searchTerm, selectedTags, selectedJobId, fixedJobId]);

  // Organize todos in single column order: Overdue > Due Soon > Due Whenever > Done
  const organizedTodos = useMemo(() => {
    const now = new Date();
    const categories: Record<TodoColumn, TodoResponse[]> = {
      overdue: [],
      'due-soon': [],
      'due-later': [],
      done: []
    };

    filteredTodos.forEach(todo => {
      if (todo.completed) {
        categories.done.push(todo);
      } else if (todo.due_date) {
        const dueDate = new Date(todo.due_date);
        const diffTime = dueDate.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays < 0) {
          categories.overdue.push(todo);
        } else if (diffDays <= 2) {
          categories['due-soon'].push(todo);
        } else {
          categories['due-later'].push(todo);
        }
      } else {
        categories['due-later'].push(todo);
      }
    });

    // Sort each category
    Object.keys(categories).forEach(key => {
      const column = key as TodoColumn;
      categories[column].sort((a, b) => {
        if (column === 'done') {
          // Sort completed todos by completion date (most recent first)
          if (a.completed_at && b.completed_at) {
            return new Date(b.completed_at).getTime() - new Date(a.completed_at).getTime();
          }
        }
        // For other columns, sort by due date, then by priority, then by creation date
        if (a.due_date && b.due_date) {
          const dateComparison = new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
          if (dateComparison !== 0) return dateComparison;
        }
        
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        const priorityComparison = priorityOrder[b.priority] - priorityOrder[a.priority];
        if (priorityComparison !== 0) return priorityComparison;
        
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
    });

    // Create single ordered list
    const orderedTodos: TodoResponse[] = [];
    const columnOrder: TodoColumn[] = ['overdue', 'due-soon', 'due-later', 'done'];
    
    columnOrder.forEach(column => {
      if (categories[column].length > 0) {
        orderedTodos.push(...categories[column]);
      }
    });

    return { categories, orderedTodos };
  }, [filteredTodos]);

  // Handle tag selection
  const toggleTag = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const clearAllTags = () => {
    setSelectedTags([]);
  };

  // Handle completion confirmation
  const handleCompletionClick = (todoId: string) => {
    setCompletionStates(prev => ({
      ...prev,
      [todoId]: prev[todoId] === 'confirming' ? 'idle' : 'confirming'
    }));
  };

  const handleConfirmCompletion = (todoId: string) => {
    onToggleComplete(todoId, true);
    setCompletionStates(prev => ({ ...prev, [todoId]: 'idle' }));
  };

  const handleCancelCompletion = (todoId: string) => {
    setCompletionStates(prev => ({ ...prev, [todoId]: 'idle' }));
  };

  const handleToggleComplete = (todoId: string, completed: boolean) => {
    onToggleComplete(todoId, completed);
    setCompletionStates(prev => ({ ...prev, [todoId]: 'idle' }));
  };

  // Handle action button visibility
  const handleToggleActions = (todoId: string) => {
    setActionStates(prev => ({ ...prev, [todoId]: !prev[todoId] }));
  };

  // Handle moving todos up/down
  const handleMoveUp = (todoId: string) => {
    const currentIndex = organizedTodos.orderedTodos.findIndex(todo => todo.id === todoId);
    if (currentIndex > 0) {
      const movedTodo = organizedTodos.orderedTodos[currentIndex];
      const displacedTodo = organizedTodos.orderedTodos[currentIndex - 1];
      if (movedTodo && displacedTodo) {
        if (onUpdate) {
          // Swap the order values via update callback
          onUpdate(movedTodo.id, { order: displacedTodo.order });
          onUpdate(displacedTodo.id, { order: movedTodo.order });
        } else if (onReorder) {
          // Compute new order of IDs and call onReorder
          const ids = [...organizedTodos.orderedTodos.map(t => t.id)];
          [ids[currentIndex - 1], ids[currentIndex]] = [ids[currentIndex], ids[currentIndex - 1]];
          onReorder(ids);
        }
      }
    }
  };

  const handleMoveDown = (todoId: string) => {
    const currentIndex = organizedTodos.orderedTodos.findIndex(todo => todo.id === todoId);
    if (currentIndex !== -1 && currentIndex < organizedTodos.orderedTodos.length - 1) {
      const movedTodo = organizedTodos.orderedTodos[currentIndex];
      const displacedTodo = organizedTodos.orderedTodos[currentIndex + 1];
      if (movedTodo && displacedTodo) {
        if (onUpdate) {
          // Swap the order values via update callback
          onUpdate(movedTodo.id, { order: displacedTodo.order });
          onUpdate(displacedTodo.id, { order: movedTodo.order });
        } else if (onReorder) {
          // Compute new order of IDs and call onReorder
          const ids = [...organizedTodos.orderedTodos.map(t => t.id)];
          [ids[currentIndex], ids[currentIndex + 1]] = [ids[currentIndex + 1], ids[currentIndex]];
          onReorder(ids);
        }
      }
    }
  };

  // Drag and drop handlers
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragOver = (event: any) => {
    const { over, active } = event;
    if (!over) {
      setOverId(null);
      return;
    }
    setOverId(over.id as string);
    // Heuristic: if moving downward (active index < over index), show placeholder AFTER; else BEFORE
    const activeIndex = organizedTodos.orderedTodos.findIndex(t => t.id === active.id);
    const overIndex = organizedTodos.orderedTodos.findIndex(t => t.id === over.id);
    setInsertAfter(activeIndex < overIndex);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setOverId(null);

    if (!over) {
      return;
    }

    const activeIndex = organizedTodos.orderedTodos.findIndex(todo => todo.id === active.id);
    const overIndex = organizedTodos.orderedTodos.findIndex(todo => todo.id === over.id);

    if (activeIndex !== overIndex) {
      // Compute new order of IDs based on drag result
      const ids = [...organizedTodos.orderedTodos.map(t => t.id)];
      const [moved] = ids.splice(activeIndex, 1);
      ids.splice(overIndex, 0, moved);

      if (onReorder) {
        onReorder(ids);
      } else if (onUpdate) {
        // Fallback: update order values sequentially
        ids.forEach((id, idx) => onUpdate(id, { order: idx }));
      }
    }
  };

  const activeTodo = activeId ? todos.find(todo => todo.id === activeId) : null;

  // Get section header for todo
  const getSectionHeader = (todo: TodoResponse, index: number) => {
    const now = new Date();
    let section = '';
    
    if (todo.completed) {
      section = 'Done';
    } else if (todo.due_date) {
      const dueDate = new Date(todo.due_date);
      const diffTime = dueDate.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays < 0) {
        section = 'Overdue';
      } else if (diffDays <= 2) {
        section = 'Due Soon';
      } else {
        section = 'Due Whenever';
      }
    } else {
      section = 'Due Whenever';
    }

    // Check if this is the first todo in this section
    if (index === 0) return section;
    
    const prevTodo = organizedTodos.orderedTodos[index - 1];
    let prevSection = '';
    
    if (prevTodo.completed) {
      prevSection = 'Done';
    } else if (prevTodo.due_date) {
      const prevDueDate = new Date(prevTodo.due_date);
      const prevDiffTime = prevDueDate.getTime() - now.getTime();
      const prevDiffDays = Math.ceil(prevDiffTime / (1000 * 60 * 60 * 24));
      
      if (prevDiffDays < 0) {
        prevSection = 'Overdue';
      } else if (prevDiffDays <= 2) {
        prevSection = 'Due Soon';
      } else {
        prevSection = 'Due Whenever';
      }
    } else {
      prevSection = 'Due Whenever';
    }

    return section !== prevSection ? section : null;
  };





  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header (hidden in embedded mode) */}
      {!embedded && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-gray-600">
              Stay organized and use 
              <span style={{ color: '#3E92CC' }}> Hired</span>
              <span style={{ color: '#1A2B3E' }}>Coach </span> 
              as your career command center.
            </p>
          </div>
          <Button onClick={onCreateNew} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Add Todo
          </Button>
        </div>
      )}

      {/* Filters (hidden in embedded mode). Keep search-only row minimal if embedded */}
      {embedded ? (
        <div className="px-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search todos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
      ) : (
        <Card>
          <CardContent className="p-4">
            <div className="space-y-4">
              {/* Search Bar - Always Visible */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search todos, tags, jobs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Filter Toggle */}
              <div className="flex items-center justify-between">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFiltersExpanded(!filtersExpanded)}
                  className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                >
                  <Filter className="h-4 w-4" />
                  Filters
                  {filtersExpanded ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
                
                {/* Active Filter Count */}
                {(selectedTags.length > 0 || selectedJobId !== 'all') && (
                  <Badge variant="secondary" className="text-xs">
                    {selectedTags.length + (selectedJobId !== 'all' ? 1 : 0)} active
                  </Badge>
                )}
              </div>

              {/* Expandable Filter Content */}
              {filtersExpanded && (
                <div className="space-y-4 pt-2 border-t border-gray-100">
                  {/* Job Filter */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Filter by Job</label>
                    <Select value={selectedJobId} onValueChange={setSelectedJobId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Filter by job" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All jobs</SelectItem>
                        <SelectItem value="general">General (no job)</SelectItem>
                        {jobs.map((job) => (
                          <SelectItem key={job.id} value={job.id}>
                            {job.company} - {job.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Tag Filter */}
                  {allTags.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-gray-700">Filter by Tags</label>
                        {selectedTags.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={clearAllTags}
                            className="text-xs"
                          >
                            Clear all
                          </Button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {allTags.map(tag => (
                          <Badge
                            key={tag}
                            variant={selectedTags.includes(tag) ? "default" : "outline"}
                            className={cn(
                              "cursor-pointer transition-all hover:scale-105",
                              selectedTags.includes(tag) && "shadow-sm"
                            )}
                            style={{
                              backgroundColor: selectedTags.includes(tag) ? getTagColor(tag) : 'transparent',
                              borderColor: getTagColor(tag),
                              color: selectedTags.includes(tag) ? 'white' : getTagColor(tag)
                            }}
                            onClick={() => toggleTag(tag)}
                          >
                            {tag.replace('-', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                            {selectedTags.includes(tag) && (
                              <X className="h-3 w-3 ml-1" />
                            )}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content - Single Column */}
      {organizedTodos.orderedTodos.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <div className="text-gray-500">
              {searchTerm || selectedTags.length > 0 || selectedJobId !== 'all' ? (
                <>
                  <Filter className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-medium mb-2">No todos match your filters</h3>
                  <p>Try adjusting your search or filter criteria</p>
                </>
              ) : (
                <>
                  <CheckSquare className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-medium mb-2">No todos yet</h3>
                  <p>Create your first todo to get started with organizing your job search</p>
                  <Button onClick={onCreateNew} className="mt-4">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Todo
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={pointerWithin}
          modifiers={[restrictToVerticalAxis]}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <div className={embedded ? "w-full" : "max-w-4xl mx-auto"}>
            <SortableContext
              items={organizedTodos.orderedTodos.map(todo => todo.id)}
              strategy={verticalListSortingStrategy}
            >
              {organizedTodos.orderedTodos.map((todo, index) => {
                const job = jobs.find(j => j.id === todo.job_id);
                const sectionHeader = getSectionHeader(todo, index);
                const completionState = completionStates[todo.id] || 'idle';
                const showActions = actionStates[todo.id] || false;
                const currentIndex = organizedTodos.orderedTodos.findIndex(t => t.id === todo.id);
                const canMoveUp = currentIndex > 0;
                const canMoveDown = currentIndex < organizedTodos.orderedTodos.length - 1;
                
                return (
                  <>
                    {/* Placeholder BEFORE hovered item when moving up */}
                    {activeId && overId === todo.id && activeId !== overId && !insertAfter && (
                      <div className="border-t-4 border-dashed border-gray-400 rounded-full my-3" />
                    )}
                    <DraggableTodoCard 
                      todo={todo} 
                      job={job}
                      index={index}
                      sectionHeader={sectionHeader}
                      categoryCounts={organizedTodos.categories}
                      completionState={completionState}
                      showActions={showActions}
                      canMoveUp={canMoveUp}
                      canMoveDown={canMoveDown}
                      onToggleActions={handleToggleActions}
                      onCompletionClick={handleCompletionClick}
                      onConfirmCompletion={handleConfirmCompletion}
                      onCancelCompletion={handleCancelCompletion}
                      onToggleComplete={handleToggleComplete}
                      onMoveUp={handleMoveUp}
                      onMoveDown={handleMoveDown}
                      onEdit={onEdit}
                      onDelete={onDelete}
                    />
                    {/* Placeholder AFTER hovered item when moving down */}
                    {activeId && overId === todo.id && activeId !== overId && insertAfter && (
                      <div className="border-t-4 border-dashed border-gray-400 rounded-full my-3" />
                    )}
                  </>
                );
              })}
            </SortableContext>
            {/* Floating preview that follows the pointer while dragging */}
            <DragOverlay adjustScale={false}>
              {activeTodo ? (
                <div className="w-full">
                  <TodoCard
                    todo={activeTodo}
                    job={jobs.find(j => j.id === activeTodo.job_id)}
                    isDragging
                  />
                </div>
              ) : null}
            </DragOverlay>
          </div>
          
        </DndContext>
      )}
      
      {/* TodoForm Modal for setting due dates */}
      {editingTodo && (
        <Dialog open={!!editingTodo} onOpenChange={() => {
          setEditingTodo(null);
          setOpenCalendar(false);
        }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit Todo</DialogTitle>
              <DialogDescription>Update details for this todo. Set due dates and reminders as needed.</DialogDescription>
            </DialogHeader>
            <TodoForm
              todo={editingTodo}
              jobs={jobs}
              onSubmit={(data) => {
                onUpdate(editingTodo.id, data);
                setEditingTodo(null);
                setOpenCalendar(false);
              }}
              onCancel={() => {
                setEditingTodo(null);
                setOpenCalendar(false);
              }}
              openCalendar={openCalendar}
              embedded
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};